import memoizeOne from 'memoize-one';
import React, { Component } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/SkMaxPrice';
import { DebounceInput } from '~/Debounce';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import ProductStatusIcon from '~/ProductStatusIcon';
import { calculatePrice } from '~/SkMaxPrice/calculatePrice';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Icon from '~/UI/Icon/Icon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';
import formatFactor from '~/ExchangeRate/formatFactor';

import styles from './styles.less';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

export interface ReferencePriceValue {
	value: string;
	parentValue: null;
	prompt: React.ReactNode;
}

export interface Values {
	referencePrice: {
		value: ReferencePriceValue | null;
		isValid: boolean;
	};
	packageSize: {
		isValid: boolean | null;
		value: string;
	};
	packageSizeForSingleUnit: {
		value: string;
		isValid: boolean | null;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceValue | null,
	packageSize: string,
	packageSizeForSingleUnit: string
) => void;
export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	skMaxPriceProduct: Product;
	usedForCalculation: boolean;
	values: Values;
	highlightProduct: boolean;
	lastSelectedProduct: boolean;
	detailLink(product: { id: string }): { pathname: string; search: string };
	historicData: boolean;
	referencePackageSize: number | null;
	style: any;
	showReferenceDataSource: boolean;
	disableSelection: boolean;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	static defaultProps = {
		highlightProduct: null,
		showReferenceDataSource: false,
		disableSelection: false,
		style: null,
	};

	resolveReferencePrice = memoizeOne((type: string | null, skMaxPriceProduct: Product): ReferencePrice | null => {
		if (type === null) {
			return null;
		}
		return (
			skMaxPriceProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ?? null
		);
	});

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			JSON.stringify(this.props.skMaxPriceProduct) !== JSON.stringify(nextProps.skMaxPriceProduct) ||
			this.props.usedForCalculation !== nextProps.usedForCalculation ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.historicData !== nextProps.historicData ||
			this.props.referencePackageSize !== nextProps.referencePackageSize ||
			this.props.style !== nextProps.style ||
			this.props.showReferenceDataSource !== nextProps.showReferenceDataSource ||
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.checked !== nextProps.checked
		);
	}

	render() {
		const {
			skMaxPriceProduct,
			usedForCalculation,
			values,
			referencePackageSize,
			highlightProduct,
			lastSelectedProduct,
			detailLink,
			historicData,
			style,
			showReferenceDataSource,
			disableSelection,
			onChange,
			onSave,
			checked,
			checkRow,
		} = this.props;
		const { product } = skMaxPriceProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(values.referencePrice.value?.value ?? null, skMaxPriceProduct);
		const prompt = referencePrice ? this.renderReferencePricePrompt(referencePrice) : null;

		return (
			<div
				className={
					product.status.isRemoved() || product.status.isBeforeIntroduction()
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<CountrySourceWithTooltip
							countryAlpha={product.dataset.source.country.alpha2}
							countryName={product.dataset.source.country.name}
							dataSourceShortName={product.dataset.source.shortName}
							dataSourceDescription={product.dataset.source.description}
						/>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{skMaxPriceProduct.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) =>
									onChange(skMaxPriceProduct, values.referencePrice.value, values.packageSize.value, value)
								}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.referencePriceCell}>
					{skMaxPriceProduct.referencePrices.length > 1 ? (
						this.renderReferencePriceSelect(prompt)
					) : (
						<div className={styles.priceCellContent}>{prompt}</div>
					)}
					{skMaxPriceProduct.singleUnitPackageSize && (
						<TailwindTooltip
							reference={<Icon name='red-exclamation-mark'>!</Icon>}
							displayClass='!absolute top-5 right-0 inline-block h-4 align-top'
							position='left'
						>
							Single unit package size
						</TailwindTooltip>
					)}
				</div>
				<div className={styles.packageForCalculationCell}>
					<div className={styles.packageInput}>
						<DebounceInput
							value={values.packageSize.value}
							onBlur={onSave}
							onChange={(value) => {
								onChange(skMaxPriceProduct, values.referencePrice.value, value, values.packageSizeForSingleUnit.value);
							}}
						>
							<Input
								id={`product-package-for-calc-${product.id}`}
								isInvalid={values.packageSize.isValid === false}
								placeholder={String(referencePackageSize || '')}
								styles={inputCompactStyles}
							/>
						</DebounceInput>
					</div>
				</div>
				<div className={styles.comparativePriceCell}>
					<div className={styles.comparativePriceCellList}>
						<Link
							data-testid='calculated-price'
							styles={{ button: styles.comparativePriceCellContent }}
							tabIndex={-1}
							to={detailLinkString}
						>
							{this.renderCalculatedPrice(referencePrice)}
						</Link>
						{skMaxPriceProduct.fxComparisonResolution !== 'not-applicable' &&
							skMaxPriceProduct.fxComparisonReferencePrice !== null && (
								<div className={styles.comparativePriceCellItem}>
									<TailwindTooltip
										reference={
											<span className='text-[10px] leading-5 text-gray-700 align-top'>
												Ex.
												<span className='absolute right-full top-0 -mr-1 -mt-1 w-3 h-3 leading-3 rounded-full text-[11px] font-semibold bg-white text-center'>
													?
												</span>
											</span>
										}
										displayClass='inline-block h-5 pr-1 align-top'
										position='left'
									>
										Price calculated based on 1{' '}
										{skMaxPriceProduct.fxComparisonReferencePrice.calculatedPrice.sourceCurrencyPrice.currency} /{' '}
										{skMaxPriceProduct.fxComparisonReferencePrice.calculatedPrice.targetCurrencyPrice.currency}{' '}
										{formatFactor(
											Number(skMaxPriceProduct.fxComparisonReferencePrice.calculatedPrice.exchangeRate.factor)
										)}{' '}
										rate of the chosen date.
									</TailwindTooltip>
									{this.renderFxReferencePrice(skMaxPriceProduct.fxComparisonReferencePrice)}
									{skMaxPriceProduct.fxComparisonResolution === 'fx-less' && (
										<div className={styles.rowPriceArrowBottom}>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='w-5 h-5'
											>
												<path
													fillRule='evenodd'
													d='M10 2a.75.75 0 01.75.75v12.59l1.95-2.1a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 111.1-1.02l1.95 2.1V2.75A.75.75 0 0110 2z'
													clipRule='evenodd'
												/>
											</svg>
										</div>
									)}
									{skMaxPriceProduct.fxComparisonResolution === 'fx-greater' && (
										<div className={styles.rowPriceArrowTop}>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='w-5 h-5'
											>
												<path
													fillRule='evenodd'
													d='M10 18a.75.75 0 01-.75-.75V4.66L7.3 6.76a.75.75 0 11-1.1-1.02l3.25-3.5a.75.75 0 011.1 0l3.25 3.5a.75.75 0 01-1.1 1.02l-1.95-2.1v12.59A.75.75 0 0110 18z'
													clipRule='evenodd'
												/>
											</svg>
										</div>
									)}
								</div>
							)}
					</div>
				</div>
				<div className={styles.selectionCell}>
					<ProductStatusIcon
						historicData={historicData}
						referenceDataSource={showReferenceDataSource ? skMaxPriceProduct.referenceDataSource : undefined}
						status={product.status}
					/>
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={values.selected && !disableSelection}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
				<div className={styles.usedForCalculationCell}>
					<Link styles={{ button: styles.usedForCalculationCellContent }} tabIndex={-1} to={detailLinkString}>
						{usedForCalculation ? <Icon name='formula' title='Used for calculation' /> : null}
					</Link>
				</div>
			</div>
		);
	}

	private renderCalculatedPrice(referencePrice: ReferencePrice | null) {
		if (referencePrice === null) {
			return null;
		}

		const {
			values: { packageSize },
			referencePackageSize,
		} = this.props;
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}
		return calculatePrice(
			referencePrice.calculatedPrice.targetCurrencyPrice,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	renderReferencePriceSelect(prompt: React.ReactNode) {
		const { skMaxPriceProduct, values, onChange } = this.props;

		return (
			<SegmentedSelect
				isValid={values.referencePrice.isValid}
				prompt={prompt}
				selectedOption={values.referencePrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) =>
					onChange(skMaxPriceProduct, value, values.packageSize.value, values.packageSizeForSingleUnit.value)
				}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						{skMaxPriceProduct.referencePrices.map((price) => (
							<SegmentedSelectOption
								key={price.calculatedPrice.type}
								prompt={this.renderReferencePricePrompt(price)}
								value={price.calculatedPrice.type}
							>
								<div>{price.calculatedPrice.description}</div>
								<div>{price.calculatedPrice.targetCurrencyPrice.roundedHumanFormat()}</div>
								<div>
									<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
								</div>
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	renderReferencePricePrompt(price: ReferencePrice) {
		return (
			<abbr data-testid='cell-reference-price' title={price.calculatedPrice.description}>
				<div>{price.calculatedPrice.targetCurrencyPrice.roundedHumanFormat()}</div>
				<div>
					<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
				</div>
			</abbr>
		);
	}

	renderFxReferencePrice(price: ReferencePrice | null) {
		if (price === null) {
			return null;
		}

		const {
			values: { packageSize },
			referencePackageSize,
		} = this.props;
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}

		return calculatePrice(
			price.calculatedPrice.targetCurrencyPrice,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
		const { skMaxPriceProduct, onSelect } = this.props;
		onSelect(skMaxPriceProduct, checked, event.nativeEvent.shiftKey);
	};
}
